import React, { FC, RefObject } from 'react';
import { ValueLabelOption } from './ValueLabelOption';
import { Autocomplete } from '@mui/lab';
import {
  Checkbox,
  InputAdornment,
  OutlinedInputProps,
  Paper,
  Theme,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import TextField from '@mui/material/TextField';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { createStyles, makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.customComponent.inputHoverBorderColor
      },
    },
    noBorder: {
      border: 'none',
    },
    autoWidth: {
      width: 'auto !important',
    },
    readOnlyInput: {
      backgroundColor: '#F3F3F3',
    },
    noMargin: {
      '& .MuiFormControl-marginDense': {
        margin: 0,
      },
    },
  })
);

export interface AutoCompleteValueLabelProps {
  name: string;
  label: string;
  variant?: 'outlined' | 'filled' | 'standard';
  required?: boolean;
  disabled?: boolean;
  size?: 'small' | 'medium';
  placeholder?: string;
  onChangeHandler?: Function;
  onBlurHandler?: Function;
  options: ValueLabelOption[];
  renderCheckboxes?: boolean;
  multiple?: boolean;
  disabledOptions?: ValueLabelOption[];
  disableClearable?: boolean;
  ref?: RefObject<HTMLInputElement>;
  InputRef?: any;
  value?: ValueLabelOption | ValueLabelOption[];
  error?: { message?: string };
  style?: object;
  isTableCellInput?: boolean;
}

export const AutoCompleteValueLabel: FC<AutoCompleteValueLabelProps> = ({
  name,
  label,
  variant = 'outlined',
  required = false,
  disabled = false,
  size = 'medium',
  placeholder = '',
  onChangeHandler,
  onBlurHandler = () => {},
  options,
  renderCheckboxes = false,
  multiple = false,
  disabledOptions = [],
  disableClearable = false,
  ref = null,
  InputRef = null,
  value,
  error,
  style,
  isTableCellInput = false,
}: AutoCompleteValueLabelProps) => {
  const classes = useStyles();

  return (
    <Autocomplete
      className={`${isTableCellInput ? classes.noMargin : ''} ${classes.root}`}
      style={style}
      id={name}
      value={value}
      ref={ref}
      placeholder={placeholder}
      multiple={multiple}
      disableCloseOnSelect={multiple}
      disableClearable={disableClearable}
      options={options}
      getOptionLabel={(option: ValueLabelOption) => {
        return option?.label ?? '';
      }}
      isOptionEqualToValue={(
        option: ValueLabelOption,
        value: ValueLabelOption
      ) => {
        return option.value === value?.value;
      }}
      disabled={disabled}
      getOptionDisabled={option => {
        return disabledOptions.some(item => item.value === option.value);
      }}
      size={size}
      onBlur={() => {
        onBlurHandler();
      }}
      onChange={(_event, value) => {
        if (onChangeHandler) {
          onChangeHandler(value);
        }
      }}
      fullWidth
      renderInput={params => {
        const inputProps: OutlinedInputProps = { ...params.InputProps };
        if (params.disabled) {
          inputProps.endAdornment = (
            <InputAdornment
              position="end"
              style={{ backgroundColor: 'inherit' }}
            >
              <Lock />
            </InputAdornment>
          );
        }
        if (isTableCellInput) {
          inputProps.classes = {
            ...inputProps.classes,
            notchedOutline: classes.noBorder,
            input: classes.autoWidth,
          };
        }
        return (
          <TextField
            {...params}
            inputRef={InputRef}
            label={label}
            value={value}
            variant={variant}
            required={required}
            error={!!error}
            helperText={error ? error.message : null}
            InputProps={inputProps}
            style={{ textOverflow: 'ellipsis' }}
            margin={isTableCellInput ? 'dense' : 'none'}
          />
        );
      }}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          {renderCheckboxes && (
            <Checkbox
              color="primary"
              inputProps={{ 'aria-label': 'primary checkbox' }}
              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
              checkedIcon={<CheckBoxIcon fontSize="small" />}
              style={{ marginRight: 8 }}
              checked={selected}
            />
          )}
          {option.label}
        </li>
      )}
      PaperComponent={({ children }) => (
        <Paper style={{ background: '#fff' }}>{children}</Paper>
      )}
    />
  );
};
